import React from "react";
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import { useTranslate } from "src/locales";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";

interface ByzMcParameterDetailRadioProps {
  mcpId: number;
  handleChange: (value: McParameterDetailResponseDto | null) => void;
  value?: McParameterDetailResponseDto | null;
  sx?: SxProps<Theme>;
  row?: boolean;
  cachedData?: McParameterDetailResponseDto[];
  cacheLoaded?: boolean;
  title?: string;
  reverseOrder?: boolean;
  disabled?: boolean;
}

export default function ByzMcParameterDetailRadio({
  mcpId,
  handleChange,
  value,
  sx,
  row = false,
  cachedData,
  cacheLoaded = false,
  title,
  reverseOrder = false,
  disabled,
}: ByzMcParameterDetailRadioProps) {
  const { t } = useTranslate();

  if (!cacheLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "56px",
          ...sx,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  const defaultOptions: McParameterDetailResponseDto[] = [
    { id: "1", mcpId, name: "Var", note: null, value: 1, orderNumber: 1 },
    { id: "0", mcpId, name: "Yok", note: null, value: 0, orderNumber: 2 },
  ];

  const options =
    cachedData && cachedData.length > 0 ? cachedData : defaultOptions;

  const sortedOptions = options
    .slice()
    .sort(
      (a, b) =>
        (a.orderNumber ?? a.value ?? Number(a.id)) -
        (b.orderNumber ?? b.value ?? Number(b.id))
    );

  const displayOptions = reverseOrder
    ? sortedOptions.slice().reverse()
    : sortedOptions;

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = event.target.value;
    const selected =
      sortedOptions.find((option) => String(option.id) === selectedId) || null;
    handleChange(selected);
  };

  return (
    <FormControl component="fieldset" sx={sx} disabled={disabled}>
      <RadioGroup
        value={String(value?.id ?? "")}
        onChange={handleRadioChange}
        row={row}
      >
        {displayOptions.map((option) => (
          <FormControlLabel
            key={option.id}
            value={String(option.id)}
            control={<Radio size="small" />}
            label={t(option.name)}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
}
